import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const WordPressOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authorization...');

  useEffect(() => {
    const handleOAuthCallback = () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Get the stored state for verification
        const storedState = sessionStorage.getItem('wordpress_oauth_state');
        const websiteId = sessionStorage.getItem('wordpress_oauth_website_id');
        const wpUrl = sessionStorage.getItem('wordpress_oauth_url');

        if (error) {
          setStatus('error');
          setMessage(errorDescription || 'Authorization was denied or failed');
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setMessage('Invalid OAuth response');
          return;
        }

        if (state !== storedState) {
          setStatus('error');
          setMessage('Invalid state parameter. Please try again.');
          return;
        }

        if (!websiteId || !wpUrl) {
          setStatus('error');
          setMessage('Session expired. Please try again.');
          return;
        }

        // Store the OAuth result for the main window to pick up
        sessionStorage.setItem('wordpress_oauth_result', JSON.stringify({
          success: true,
          code: code,
          state: state
        }));

        setStatus('success');
        setMessage('Authorization successful! You can close this window.');

        // Close the window after a short delay
        setTimeout(() => {
          window.close();
        }, 2000);

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred');
      }
    };

    handleOAuthCallback();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full mx-auto p-6">
        <div className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
              <h1 className="text-xl font-semibold">Connecting WordPress</h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
              <h1 className="text-xl font-semibold text-green-600">Success!</h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <h1 className="text-xl font-semibold text-red-600">Authorization Failed</h1>
              <p className="text-muted-foreground">{message}</p>
              <button
                onClick={() => window.close()}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Close Window
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WordPressOAuthCallback;
